import React, { useRef } from "react";

import { cn } from "../../../ui";

type Props = {
  length?: number;
  value: string[];
  onChange: (next: string[]) => void;
  error?: boolean;
  disabled?: boolean;
};

const OtpInput: React.FC<Props> = ({
  length = 6,
  value,
  onChange,
  error,
  disabled,
}) => {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  const setDigit = (index: number, digit: string) => {
    const next = [...value];
    next[index] = digit;
    onChange(next);
  };

  const handleChange = (index: number, raw: string) => {
    const digit = raw.replace(/\D/g, "").slice(-1);
    if (!digit && raw !== "") return;
    setDigit(index, digit);
    if (digit && index < length - 1) refs.current[index + 1]?.focus();
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === "Backspace" && !value[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0) refs.current[index - 1]?.focus();
    if (e.key === "ArrowRight" && index < length - 1)
      refs.current[index + 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!pasted) return;
    const next = new Array(length).fill("");
    for (let i = 0; i < pasted.length && i < length; i += 1) next[i] = pasted[i];
    onChange(next);
    const focusAt = Math.min(pasted.length, length - 1);
    refs.current[focusAt]?.focus();
  };

  return (
    <div className="flex items-center justify-between gap-2">
      {Array.from({ length }).map((_, index) => {
        const digit = value[index] ?? "";
        const filled = digit !== "";
        return (
          <input
            key={index}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={index === 0 ? "one-time-code" : "off"}
            maxLength={1}
            value={digit}
            disabled={disabled}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={index === 0 ? handlePaste : undefined}
            onFocus={(e) => e.target.select()}
            className={cn(
              "h-12 w-11 rounded-xl border bg-white text-center text-lg font-semibold tracking-tight text-gray-900 shadow-sm transition-colors",
              "focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              error
                ? "border-red-300 bg-red-50 text-red-700"
                : filled
                ? "border-brand/60 bg-brand/5 text-brand"
                : "border-gray-200",
            )}
          />
        );
      })}
    </div>
  );
};

export default OtpInput;
